import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  Cog6ToothIcon,
  TrashIcon,
  LinkIcon,
  XMarkIcon,
  ClipboardIcon,
  PencilIcon,
  UserIcon,
  UserMinusIcon,
  CameraIcon,
  UsersIcon,
  ShareIcon,
  CakeIcon,
  CalendarIcon,
  PlusIcon,
  SparklesIcon,
  CheckIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore } from '../../store/familyStore';
import api from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import MemberAvatar from '../../components/common/MemberAvatar';
import Cropper, { Area } from 'react-easy-crop';

interface ManagedUserForm {
  name: string;
  role: string;
  email: string;
  ui_mode: string;
  color: string;
}

interface SpecialDate {
  id: string;
  date_type: string;
  date: string;
  name: string | null;
  is_recurring: boolean;
}

// Ring icon for anniversaries
const RingIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v4" />
    <path d="M9 3l3 3 3-3" />
  </svg>
);

const specialDateConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string; emoji: string }> = {
  birthday: { icon: CakeIcon, color: 'text-pink-400', label: 'Birthday', emoji: '🎂' },
  nameday: { icon: CalendarIcon, color: 'text-purple-400', label: 'Name Day', emoji: '📅' },
  anniversary: { icon: RingIcon, color: 'text-amber-400', label: 'Anniversary', emoji: '💍' },
  wedding: { icon: RingIcon, color: 'text-amber-400', label: 'Wedding Anniversary', emoji: '💒' },
  other: { icon: SparklesIcon, color: 'text-blue-400', label: 'Other', emoji: '✨' },
};

interface MemberSettingsForm {
  role: string;
  ui_mode: string;
  nickname: string;
}

export default function FamilyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentFamily, fetchFamily, deleteFamily, setCurrentFamily, isLoading } = useFamilyStore();
  const { user } = useAuthStore();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInviteOptionsModal, setShowInviteOptionsModal] = useState(false);
  const [showEmailInviteModal, setShowEmailInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRecipientName, setInviteRecipientName] = useState('');
  const [showAddManagedModal, setShowAddManagedModal] = useState(false);
  const [showMemberSettings, setShowMemberSettings] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState('');
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [isSendingEmailInvite, setIsSendingEmailInvite] = useState(false);
  
  // Avatar cropper state for managed users
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  
  // Linked families state
  const [linkedFamilies, setLinkedFamilies] = useState<any[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [familyLinkCode, setFamilyLinkCode] = useState('');
  const [isCreatingLinkInvite, setIsCreatingLinkInvite] = useState(false);
  const [showLinkEmailModal, setShowLinkEmailModal] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkRecipientName, setLinkRecipientName] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [isSendingLinkEmail, setIsSendingLinkEmail] = useState(false);
  
  // Special dates state for managed users
  const [memberSpecialDates, setMemberSpecialDates] = useState<SpecialDate[]>([]);
  const [loadingMemberDates, setLoadingMemberDates] = useState(false);
  const [showAddMemberDateModal, setShowAddMemberDateModal] = useState(false);
  const [newMemberDateType, setNewMemberDateType] = useState('birthday');
  const [newMemberDate, setNewMemberDate] = useState('');
  const [newMemberDateName, setNewMemberDateName] = useState('');
  
  // Shared members state
  const [showShareMemberModal, setShowShareMemberModal] = useState<string | null>(null);
  const [shareableFamilies, setShareableFamilies] = useState<{id: string; name: string; my_role: string; source: string}[]>([]);
  const [loadingShareableFamilies, setLoadingShareableFamilies] = useState(false);
  const [pendingShares, setPendingShares] = useState<any[]>([]);
  const [loadingPendingShares, setLoadingPendingShares] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [memberShares, setMemberShares] = useState<Record<string, {id: string; target_family_name: string; status: string}[]>>({});

  const { register: registerManaged, handleSubmit: handleSubmitManaged, reset: resetManaged, formState: { errors: managedErrors } } = useForm<ManagedUserForm>({
    defaultValues: { role: 'child', ui_mode: 'simple' }
  });

  const { register: registerSettings, handleSubmit: handleSubmitSettings, reset: resetSettings, setValue: setSettingsValue } = useForm<MemberSettingsForm>();

  useEffect(() => {
    if (id) {
      fetchFamily(id);
      loadLinkedFamilies();
    }
  }, [id, fetchFamily]);
  
  const loadLinkedFamilies = async () => {
    if (!id) return;
    setLoadingLinked(true);
    try {
      const response = await api.get(`/family-links/${id}/linked`);
      setLinkedFamilies(response.data || []);
    } catch (error) {
      console.error('Failed to load linked families:', error);
    } finally {
      setLoadingLinked(false);
    }
  };
  
  const handleCreateFamilyLinkInvite = async () => {
    if (!id) return;
    setIsCreatingLinkInvite(true);
    try {
      const response = await api.post(`/family-links/${id}/link-invitations`, {
        expires_in_days: 7,
      });
      setFamilyLinkCode(response.data.invite_link);
      setShowLinkModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create link invitation');
    } finally {
      setIsCreatingLinkInvite(false);
    }
  };
  
  // Load pending share requests
  const loadPendingShares = useCallback(async () => {
    if (!id) return;
    setLoadingPendingShares(true);
    try {
      const response = await api.get(`/families/${id}/pending-shares`);
      setPendingShares(response.data || []);
    } catch (error) {
      console.error('Failed to load pending shares:', error);
    } finally {
      setLoadingPendingShares(false);
    }
  }, [id]);

  // Load shares for all managed members
  const loadMemberShares = useCallback(async () => {
    if (!id || !currentFamily?.members) return;
    
    const managedMembers = currentFamily.members.filter((m: any) => m.is_managed && !m.is_shared_copy);
    const sharesMap: Record<string, any[]> = {};
    
    for (const member of managedMembers) {
      try {
        const response = await api.get(`/families/${id}/members/${member.id}/shares`);
        if (response.data && response.data.length > 0) {
          sharesMap[member.id] = response.data;
        }
      } catch (error) {
        console.error(`Failed to load shares for member ${member.id}:`, error);
      }
    }
    
    setMemberShares(sharesMap);
  }, [id, currentFamily?.members]);

  useEffect(() => {
    if (id) {
      loadPendingShares();
    }
  }, [id, loadPendingShares]);

  useEffect(() => {
    if (currentFamily?.members) {
      loadMemberShares();
    }
  }, [currentFamily?.members, loadMemberShares]);

  // Load shareable families when opening share modal
  const handleOpenShareModal = async (memberId: string) => {
    setShowShareMemberModal(memberId);
    setLoadingShareableFamilies(true);
    try {
      const response = await api.get(`/families/${id}/shareable-families`);
      setShareableFamilies(response.data || []);
    } catch (error) {
      console.error('Failed to load shareable families:', error);
      toast.error('Failed to load families');
    } finally {
      setLoadingShareableFamilies(false);
    }
  };

  // Share member with another family
  const handleShareMember = async (targetFamilyId: string) => {
    if (!id || !showShareMemberModal) return;
    try {
      await api.post(`/families/${id}/share-member`, {
        member_id: showShareMemberModal,
        target_family_id: targetFamilyId,
        message: shareMessage || undefined,
      });
      toast.success('Share request sent!');
      setShowShareMemberModal(null);
      setShareMessage('');
      // Refresh member shares to show the new pending share
      loadMemberShares();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to share member');
    }
  };

  // Respond to share request
  const handleRespondToShare = async (shareId: string, accept: boolean) => {
    if (!id) return;
    try {
      await api.post(`/families/${id}/respond-share/${shareId}`, { accept });
      toast.success(accept ? 'Share request accepted!' : 'Share request rejected');
      loadPendingShares();
      fetchFamily(id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to respond to share request');
    }
  };

  const handleUnlinkFamily = async (linkId: string) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to unlink this family? All shared resources will be unshared.')) {
      return;
    }
    try {
      await api.delete(`/family-links/${id}/linked/${linkId}`);
      toast.success('Family unlinked');
      loadLinkedFamilies();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unlink family');
    }
  };
  
  const handleCopyFamilyLink = () => {
    navigator.clipboard.writeText(familyLinkCode);
    toast.success('Link copied to clipboard!');
  };

  const handleSendLinkEmail = async () => {
    if (!id || !linkEmail.trim()) return;
    setIsSendingLinkEmail(true);
    try {
      const response = await api.post(`/family-links/${id}/link-invitations/send-email`, {
        email: linkEmail.trim(),
        recipient_name: linkRecipientName.trim() || undefined,
        message: linkMessage.trim() || undefined,
        expires_in_days: 7,
      });
      if (response.data.email_sent) {
        toast.success(`Family link invitation sent to ${linkEmail.trim()}!`);
      } else {
        toast.success('Link created but email could not be sent');
        setFamilyLinkCode(response.data.invite_link);
        setShowLinkModal(true);
      }
      setShowLinkEmailModal(false);
      setLinkEmail('');
      setLinkRecipientName('');
      setLinkMessage('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send link invitation email');
    } finally {
      setIsSendingLinkEmail(false);
    }
  };
  
  // Image cropping helpers
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) throw new Error('No 2d context');

      const size = Math.max(pixelCrop.width, pixelCrop.height);
      canvas.width = size;
      canvas.height = size;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, size, size);

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        size,
        size
      );

      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas is empty'));
        }, 'image/jpeg', 0.9);
      });
    },
    []
  );

  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result as string);
      setShowAvatarCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    
    // Reset input so the same file can be selected again
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const handleUploadManagedAvatar = async () => {
    if (!avatarPreview || !croppedAreaPixels || !showMemberSettings || !id) return;
    
    setAvatarUploading(true);
    try {
      const blob = await getCroppedImg(avatarPreview, croppedAreaPixels);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/families/${id}/managed-users/${showMemberSettings}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Avatar updated!');
      setShowAvatarCropper(false);
      setAvatarPreview(null);
      fetchFamily(id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!id) return;
    setIsCreatingInvite(true);
    try {
      const payload: any = {
        role: 'member',
        expires_in_days: 7,
      };
      if (inviteEmail.trim()) {
        payload.email = inviteEmail.trim();
      }
      const response = await api.post(`/families/${id}/invitations`, payload);
      setInviteLink(response.data.invite_link);
      if (inviteEmail.trim()) {
        toast.success(`Invitation sent to ${inviteEmail.trim()}`);
        setInviteEmail('');
      } else {
        toast.success('Invitation link created');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Link copied to clipboard!');
  };

  const handleSendEmailInvite = async () => {
    if (!id || !inviteEmail.trim()) return;
    setIsSendingEmailInvite(true);
    try {
      const response = await api.post(`/families/${id}/invitations/send-email`, {
        email: inviteEmail.trim(),
        recipient_name: inviteRecipientName.trim() || undefined,
        role: 'member',
        expires_in_days: 7,
      });
      if (response.data.email_sent) {
        toast.success(`Invitation email sent to ${inviteEmail.trim()}!`);
      } else {
        toast.success('Invitation created but email could not be sent');
        setInviteLink(response.data.invite_link);
      }
      setShowEmailInviteModal(false);
      setInviteEmail('');
      setInviteRecipientName('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation email');
    } finally {
      setIsSendingEmailInvite(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !currentFamily) return;
    if (!window.confirm(`Are you sure you want to delete "${currentFamily.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteFamily(id);
      toast.success('Family deleted');
      navigate('/families');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete family');
    }
  };

  const handleSetAsCurrent = () => {
    if (currentFamily) {
      setCurrentFamily(currentFamily);
      toast.success(`${currentFamily.name} set as current family`);
    }
  };

  const handleCreateManagedUser = async (data: ManagedUserForm) => {
    if (!id) return;
    try {
      await api.post(`/families/${id}/managed-users`, {
        name: data.name,
        role: data.role,
        email: data.email || undefined,
        ui_mode: data.ui_mode,
        color: data.color || undefined,
      });
      toast.success('Family member added!');
      setShowAddManagedModal(false);
      resetManaged();
      fetchFamily(id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleUpdateMember = async (memberId: string, data: MemberSettingsForm) => {
    if (!id) return;
    try {
      const member = currentFamily?.members?.find(m => m.id === memberId);
      const endpoint = member?.is_managed 
        ? `/families/${id}/managed-users/${memberId}`
        : `/families/${id}/members/${memberId}`;
      
      await api.patch(endpoint, {
        role: data.role,
        ui_mode: data.ui_mode,
        nickname: data.nickname || undefined,
      });
      toast.success('Member updated!');
      setShowMemberSettings(null);
      fetchFamily(id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!id) return;
    const member = currentFamily?.members?.find(m => m.id === memberId);
    const memberName = member?.nickname || member?.first_name || 'this member';
    if (!window.confirm(`Remove ${memberName} from the family? This action cannot be undone.`)) return;
    try {
      await api.delete(`/families/${id}/members/${memberId}`);
      toast.success('Member removed');
      setShowMemberSettings(null); // Close modal if open
      fetchFamily(id);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const openMemberSettings = (member: any) => {
    setSettingsValue('role', member.role);
    setSettingsValue('ui_mode', member.ui_mode || 'advanced');
    setSettingsValue('nickname', member.nickname || '');
    setShowMemberSettings(member.id);
    // Load special dates for this member
    loadMemberSpecialDates(member);
  };

  const loadMemberSpecialDates = async (member: any) => {
    if (!id) return;
    setLoadingMemberDates(true);
    try {
      const response = await api.get(`/gifts/special-dates/family/${id}`);
      // Filter dates for this specific member
      const memberDates = response.data.filter((sd: any) => 
        (member.user_id && sd.user_id === member.user_id) ||
        (member.is_managed && sd.family_member_id === member.id)
      );
      setMemberSpecialDates(memberDates);
    } catch (error) {
      console.error('Failed to load member special dates:', error);
    } finally {
      setLoadingMemberDates(false);
    }
  };

  const handleAddMemberSpecialDate = async () => {
    if (!newMemberDate || !showMemberSettings) {
      toast.error('Please select a date');
      return;
    }
    
    const selectedMember = currentFamily?.members?.find((m: any) => m.id === showMemberSettings);
    if (!selectedMember) return;

    try {
      await api.post('/gifts/special-dates', {
        date_type: newMemberDateType,
        date: new Date(newMemberDate).toISOString(),
        name: newMemberDateName || null,
        is_recurring: true,
        family_member_id: selectedMember.is_managed ? selectedMember.id : undefined,
      });
      toast.success('Special date added!');
      setShowAddMemberDateModal(false);
      setNewMemberDate('');
      setNewMemberDateName('');
      setNewMemberDateType('birthday');
      loadMemberSpecialDates(selectedMember);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add special date');
    }
  };

  const handleDeleteMemberSpecialDate = async (dateId: string) => {
    if (!confirm('Delete this special date?')) return;
    const selectedMember = currentFamily?.members?.find((m: any) => m.id === showMemberSettings);
    try {
      await api.delete(`/gifts/special-dates/${dateId}`);
      toast.success('Special date deleted');
      if (selectedMember) {
        loadMemberSpecialDates(selectedMember);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete special date');
    }
  };

  const formatSpecialDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  };

  const roleColors: Record<string, string> = {
    owner: 'from-primary-500 to-accent-500',
    admin: 'from-amber-500 to-orange-500',
    member: 'from-emerald-500 to-teal-500',
    child: 'from-cyan-500 to-blue-500',
    guest: 'from-surface-500 to-surface-600',
  };

  const memberColors = [
    '#10b981', '#14b8a6', '#0ea5e9', '#6366f1', '#8b5cf6',
    '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308',
  ];

  if (isLoading || !currentFamily) {
    return (
      <div className="page-content flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/families')} className="btn-icon">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{currentFamily.name}</h1>
          {currentFamily.description && (
            <p className="text-surface-400">{currentFamily.description}</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 mb-8">
        <button 
          onClick={handleSetAsCurrent} 
          className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-white transition-colors"
          title="Set as Current"
        >
          <CheckIcon className="w-3.5 h-3.5" />
          <span className="sm:hidden">Set</span>
          <span className="hidden sm:inline">Set as Current</span>
        </button>
        <button
          onClick={() => setShowInviteOptionsModal(true)}
          className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-accent-400 transition-colors"
          title="Invite Member"
        >
          <UserPlusIcon className="w-3.5 h-3.5" />
          <span className="sm:hidden">Invite</span>
          <span className="hidden sm:inline">Invite</span>
        </button>
        <button
          onClick={() => setShowAddManagedModal(true)}
          className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-white transition-colors"
          title="Add Member"
        >
          <UserIcon className="w-3.5 h-3.5" />
          <span className="sm:hidden">Add</span>
          <span className="hidden sm:inline">Add Member</span>
        </button>
      </div>

      {/* Members */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">
          Members ({currentFamily.members?.length || 0})
        </h2>
        <div className="grid gap-3">
          {currentFamily.members?.map((member: any, index: number) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card cursor-pointer hover:bg-surface-700/50 transition-colors"
              onClick={() => navigate(`/families/${id}/members/${member.id}`)}
            >
              <div className="flex items-center gap-4">
                <MemberAvatar
                  avatarUrl={member.user_id === user?.id ? user?.avatar_url : member.avatar_url}
                  name={member.nickname || member.first_name || '?'}
                  color={member.color}
                  role={member.role}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-white truncate">
                      {member.nickname || `${member.first_name} ${member.last_name || ''}`}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-surface-300">
                      {member.role}
                    </span>
                    {member.is_managed && !member.is_shared_copy && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">
                        managed
                      </span>
                    )}
                    {member.is_shared_copy && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500/20 text-accent-300" title={member.source_family_name ? `Shared from ${member.source_family_name}` : 'Shared member'}>
                        shared
                      </span>
                    )}
                    {/* Only show simple UI tag for real user accounts, not managed members */}
                    {!member.is_managed && member.ui_mode === 'simple' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                        simple UI
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-surface-400">
                    {member.is_managed ? (member.managed_email || 'No account') : `@${member.username}`}
                  </p>
                  {/* Show shared families for managed members */}
                  {member.is_managed && !member.is_shared_copy && memberShares[member.id] && memberShares[member.id].length > 0 && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <span className="text-xs text-surface-500">Shared with:</span>
                      {memberShares[member.id].map((share: any) => (
                        <span 
                          key={share.id} 
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            share.status === 'accepted' 
                              ? 'bg-emerald-500/20 text-emerald-300' 
                              : share.status === 'pending'
                              ? 'bg-amber-500/20 text-amber-300'
                              : 'bg-red-500/20 text-red-300'
                          }`}
                          title={`Status: ${share.status}`}
                        >
                          {share.target_family_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Action buttons at bottom */}
              <div className="flex items-center gap-1.5 pt-3 mt-3 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    openMemberSettings(member);
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-white transition-colors"
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                  Edit
                </button>
                {/* Share button for managed members (not shared copies) */}
                {member.is_managed && !member.is_shared_copy && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenShareModal(member.id);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-accent-400 transition-colors"
                  >
                    <ShareIcon className="w-3.5 h-3.5" />
                    Share
                  </button>
                )}
                {member.role !== 'owner' && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveMember(member.id);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-surface-700 hover:bg-surface-600 text-xs text-surface-300 hover:text-red-400 transition-colors"
                  >
                    <UserMinusIcon className="w-3.5 h-3.5" />
                    Remove
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Pending Share Requests */}
      {pendingShares.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Pending Member Shares</h2>
              <p className="text-surface-400 text-sm">Other families want to share members with you</p>
            </div>
          </div>
          <div className="grid gap-3">
            {pendingShares.map((share: any) => (
              <motion.div
                key={share.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card flex items-center gap-4 border-l-4 border-l-amber-500"
              >
                <div className="w-12 h-12 rounded-full bg-surface-600 flex items-center justify-center overflow-hidden">
                  {share.source_member_avatar ? (
                    <img src={share.source_member_avatar} alt={share.source_member_name} className="w-full h-full object-cover" />
                  ) : (
                    <UserIcon className="w-6 h-6 text-surface-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{share.source_member_name}</p>
                  <p className="text-sm text-surface-400">
                    From <span className="text-accent-400">{share.source_family_name}</span> by {share.shared_by_name}
                  </p>
                  {share.message && (
                    <p className="text-xs text-surface-500 mt-1 italic">"{share.message}"</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRespondToShare(share.id, true)}
                    className="btn-primary px-3 py-1.5 text-sm"
                  >
                    <CheckIcon className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Accept</span>
                  </button>
                  <button
                    onClick={() => handleRespondToShare(share.id, false)}
                    className="btn-secondary px-3 py-1.5 text-sm"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Reject</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Families */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Linked Families</h2>
            <p className="text-surface-400 text-sm">Families connected to share resources</p>
          </div>
          <button
            onClick={handleCreateFamilyLinkInvite}
            disabled={isCreatingLinkInvite}
            className="btn-secondary flex items-center gap-2"
          >
            <LinkIcon className="w-5 h-5" />
            <span>{isCreatingLinkInvite ? 'Creating...' : 'Link Family'}</span>
          </button>
        </div>
        
        {loadingLinked ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : linkedFamilies.length === 0 ? (
          <div className="card text-center py-8 border-dashed border-2 border-surface-600">
            <UsersIcon className="w-12 h-12 mx-auto text-surface-500 mb-3" />
            <p className="text-surface-400 mb-2">No linked families yet</p>
            <p className="text-surface-500 text-sm">
              Link with another family to share passwords, wishlists, and more
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {linkedFamilies.map((link: any) => (
              <motion.div
                key={link.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card flex items-center gap-4 border-l-4 border-l-accent-500"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center">
                  <UsersIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white">{link.family_name}</h3>
                  <p className="text-sm text-surface-400">
                    {link.member_count} members • Linked by {link.linked_by_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUnlinkFamily(link.id)}
                    className="btn-icon text-surface-400 hover:text-red-400"
                    title="Unlink family"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card border-red-500/20">
        <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
        <button onClick={handleDelete} className="btn-danger">
          <TrashIcon className="w-5 h-5" />
          <span>Delete Family</span>
        </button>
      </div>

      {/* Family Link Modal */}
      <AnimatePresence>
        {showLinkModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowLinkModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Link Another Family</h2>
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-surface-300">
                  Share this link with an admin from another family. When they click it and accept, 
                  the families will be linked and can share resources.
                </p>
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-primary-400" />
                  <span className="text-surface-300">Share this link:</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={familyLinkCode}
                    readOnly
                    className="input flex-1 text-sm"
                  />
                  <button onClick={handleCopyFamilyLink} className="btn-primary">
                    <ClipboardIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-surface-400">
                  This link expires in 7 days
                </p>

                <div className="border-t border-surface-700 pt-4 mt-4">
                  <button
                    onClick={() => {
                      setShowLinkModal(false);
                      setLinkEmail('');
                      setLinkRecipientName('');
                      setLinkMessage('');
                      setShowLinkEmailModal(true);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors"
                  >
                    <EnvelopeIcon className="w-5 h-5 text-accent-400" />
                    <span className="text-surface-300">Or send via email instead</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Link Email Modal */}
      <AnimatePresence>
        {showLinkEmailModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowLinkEmailModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Send Link Invitation</h2>
                <button
                  onClick={() => setShowLinkEmailModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                  <p className="text-sm text-surface-300">
                    🔗 Send a beautiful email inviting another family to connect with <strong className="text-white">{currentFamily?.name}</strong>
                  </p>
                </div>

                <div className="input-group">
                  <label className="input-label">Email Address *</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="family-admin@example.com"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Recipient Name (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Their name for a personal touch"
                    value={linkRecipientName}
                    onChange={(e) => setLinkRecipientName(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Personal Message (optional)</label>
                  <textarea
                    className="input min-h-[80px]"
                    placeholder="Add a personal note to your invitation..."
                    value={linkMessage}
                    onChange={(e) => setLinkMessage(e.target.value)}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowLinkEmailModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendLinkEmail}
                    disabled={isSendingLinkEmail || !linkEmail.trim()}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    {isSendingLinkEmail ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <EnvelopeIcon className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowInviteModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Invite Member</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {isCreatingInvite ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : inviteLink ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-primary-400" />
                    <span className="text-surface-300">Share this link:</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="input flex-1 text-sm"
                    />
                    <button onClick={handleCopyLink} className="btn-primary">
                      <ClipboardIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-surface-400">
                    This link expires in 7 days
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5 text-accent-400" />
                    <span className="text-surface-300">Invite by email:</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="input flex-1 text-sm"
                    />
                    <button 
                      onClick={handleCreateInvite} 
                      className="btn-primary" 
                      disabled={isCreatingInvite || !inviteEmail.trim()}
                    >
                      {isCreatingInvite ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Invite Options Modal */}
      <AnimatePresence>
        {showInviteOptionsModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowInviteOptionsModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Invite Member</h2>
                <button
                  onClick={() => setShowInviteOptionsModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowInviteOptionsModal(false);
                    setShowInviteModal(true);
                    handleCreateInvite();
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">Invite by Link</h3>
                    <p className="text-sm text-surface-400">Generate a shareable invitation link</p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setShowInviteOptionsModal(false);
                    setInviteEmail('');
                    setInviteRecipientName('');
                    setShowEmailInviteModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5 text-accent-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">Invite by Email</h3>
                    <p className="text-sm text-surface-400">Send a beautiful invitation email</p>
                  </div>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Email Invite Modal */}
      <AnimatePresence>
        {showEmailInviteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowEmailInviteModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Send Email Invitation</h2>
                <button
                  onClick={() => setShowEmailInviteModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20">
                  <p className="text-sm text-surface-300">
                    📧 We'll send a beautiful invitation email to join <strong className="text-white">{currentFamily?.name}</strong>
                  </p>
                </div>

                <div className="input-group">
                  <label className="input-label">Email Address *</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="friend@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Recipient Name (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Their name for a personal touch"
                    value={inviteRecipientName}
                    onChange={(e) => setInviteRecipientName(e.target.value)}
                  />
                  <p className="text-xs text-surface-500 mt-1">If provided, the email will greet them by name</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowEmailInviteModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmailInvite}
                    disabled={isSendingEmailInvite || !inviteEmail.trim()}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    {isSendingEmailInvite ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <EnvelopeIcon className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Managed User Modal */}
      <AnimatePresence>
        {showAddManagedModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAddManagedModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Family Member</h2>
                <button onClick={() => setShowAddManagedModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <p className="text-surface-400 text-sm mb-4">
                Add a family member who doesn't have an account yet (e.g., kids). 
                They can link their account later when they register.
              </p>

              <form onSubmit={handleSubmitManaged(handleCreateManagedUser)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Child's name"
                    {...registerManaged('name', { required: 'Name is required' })}
                  />
                  {managedErrors.name && <p className="text-sm text-red-400">{managedErrors.name.message}</p>}
                </div>

                <div className="input-group">
                  <label className="input-label">Role</label>
                  <select className="input" {...registerManaged('role')}>
                    <option value="child">Child</option>
                    <option value="member">Member</option>
                    <option value="guest">Guest</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">UI Mode</label>
                  <select className="input" {...registerManaged('ui_mode')}>
                    <option value="simple">Simple (for kids)</option>
                    <option value="advanced">Advanced (full features)</option>
                  </select>
                  <p className="text-xs text-surface-500 mt-1">
                    Simple mode shows a streamlined interface
                  </p>
                </div>

                <div className="input-group">
                  <label className="input-label">Email (optional)</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="For future account linking"
                    {...registerManaged('email')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {memberColors.map((color) => (
                      <label key={color} className="cursor-pointer">
                        <input
                          type="radio"
                          value={color}
                          className="sr-only peer"
                          {...registerManaged('color')}
                        />
                        <div
                          className="w-8 h-8 rounded-lg border-2 border-transparent peer-checked:border-white transition-all"
                          style={{ backgroundColor: color }}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowAddManagedModal(false)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Add Member
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Member Settings Modal */}
      <AnimatePresence>
        {showMemberSettings && (() => {
          const selectedMember = currentFamily?.members?.find(m => m.id === showMemberSettings);
          return (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowMemberSettings(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Member Settings</h2>
                <button onClick={() => setShowMemberSettings(null)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Avatar Upload for Managed Users */}
              {selectedMember?.is_managed && (
                <div className="flex flex-col items-center mb-6">
                  <div className="relative group">
                    <MemberAvatar
                      avatarUrl={selectedMember.avatar_url}
                      name={selectedMember.nickname || selectedMember.first_name || '?'}
                      color={selectedMember.color}
                      role={selectedMember.role}
                      size="xl"
                    />
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <CameraIcon className="w-6 h-6 text-white" />
                    </button>
                  </div>
                  <p className="text-xs text-surface-400 mt-2">Click to change avatar</p>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarFileSelect}
                  />
                </div>
              )}

              <form onSubmit={handleSubmitSettings((data) => handleUpdateMember(showMemberSettings, data))} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Nickname</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Display name"
                    {...registerSettings('nickname')}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Role</label>
                  <select className="input" {...registerSettings('role')}>
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="child">Child</option>
                    <option value="guest">Guest</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">UI Mode</label>
                  <select className="input" {...registerSettings('ui_mode')}>
                    <option value="simple">Simple (streamlined)</option>
                    <option value="advanced">Advanced (all features)</option>
                  </select>
                  <p className="text-xs text-surface-500 mt-1">
                    Simple mode is great for kids or users who want fewer options
                  </p>
                </div>

                {/* Special Dates Section */}
                <div className="pt-4 border-t border-surface-700">
                  <div className="flex items-center justify-between mb-3">
                    <label className="input-label !mb-0">Important Dates</label>
                    <button
                      type="button"
                      onClick={() => setShowAddMemberDateModal(true)}
                      className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  {loadingMemberDates ? (
                    <div className="flex justify-center py-4">
                      <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                    </div>
                  ) : memberSpecialDates.length === 0 ? (
                    <p className="text-sm text-surface-500 text-center py-3">
                      No important dates yet
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {memberSpecialDates.map((sd) => {
                        const config = specialDateConfig[sd.date_type] || specialDateConfig.other;
                        const DateIcon = config.icon;
                        return (
                          <div
                            key={sd.id}
                            className="flex items-center gap-2 p-2 rounded-lg bg-surface-800"
                          >
                            <DateIcon className={`w-4 h-4 ${config.color}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">
                                {sd.name || config.label}
                              </p>
                              <p className="text-xs text-surface-400">{formatSpecialDate(sd.date)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteMemberSpecialDate(sd.id)}
                              className="p-1 text-surface-500 hover:text-red-400"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => handleRemoveMember(showMemberSettings)} 
                    className="btn-danger"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                  <button type="button" onClick={() => setShowMemberSettings(null)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        );})()}
      </AnimatePresence>

      {/* Avatar Cropper Modal */}
      <AnimatePresence>
        {showAvatarCropper && avatarPreview && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => {
                setShowAvatarCropper(false);
                setAvatarPreview(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Crop Avatar</h3>
                <button
                  onClick={() => {
                    setShowAvatarCropper(false);
                    setAvatarPreview(null);
                  }}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              <div className="relative w-full aspect-square bg-surface-800 rounded-2xl overflow-hidden">
                <Cropper
                  image={avatarPreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                />
              </div>
              
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-surface-400">Zoom</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 accent-primary-500"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAvatarCropper(false);
                      setAvatarPreview(null);
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={avatarUploading}
                    onClick={handleUploadManagedAvatar}
                    className="btn-primary flex-1"
                  >
                    {avatarUploading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Member Special Date Modal */}
      <AnimatePresence>
        {showAddMemberDateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              style={{ zIndex: 60 }}
              onClick={() => setShowAddMemberDateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content max-w-md"
              style={{ zIndex: 61 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Important Date</h2>
                <button onClick={() => setShowAddMemberDateModal(false)} className="btn-icon">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Type</label>
                  <select
                    className="input"
                    value={newMemberDateType}
                    onChange={(e) => setNewMemberDateType(e.target.value)}
                  >
                    <option value="birthday">🎂 Birthday</option>
                    <option value="nameday">📅 Name Day</option>
                    <option value="anniversary">💍 Anniversary</option>
                    <option value="wedding">💒 Wedding Anniversary</option>
                    <option value="other">✨ Other</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Date</label>
                  <input
                    type="date"
                    className="input"
                    value={newMemberDate}
                    onChange={(e) => setNewMemberDate(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Custom Name (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Sweet 16"
                    value={newMemberDateName}
                    onChange={(e) => setNewMemberDateName(e.target.value)}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddMemberDateModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddMemberSpecialDate}
                    className="btn-primary flex-1"
                  >
                    Add Date
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Share Member Modal */}
      <AnimatePresence>
        {showShareMemberModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              style={{ zIndex: 60 }}
              onClick={() => {
                setShowShareMemberModal(null);
                setShareMessage('');
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content max-w-md"
              style={{ zIndex: 61 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Share Member</h2>
                <button 
                  onClick={() => {
                    setShowShareMemberModal(null);
                    setShareMessage('');
                  }} 
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <p className="text-surface-400 mb-4">
                Share this family member with another family you belong to. They will appear in both families.
              </p>

              <div className="space-y-4">
                <div className="input-group">
                  <label className="input-label">Message (optional)</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., This is our shared mom"
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Select Family</label>
                  {loadingShareableFamilies ? (
                    <div className="flex justify-center py-4">
                      <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                    </div>
                  ) : shareableFamilies.length === 0 ? (
                    <div className="text-center py-4 text-surface-400">
                      <p>No families available to share with.</p>
                      <p className="text-sm mt-1">Join another family or link with one first.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {shareableFamilies.map((family) => {
                        const existingShare = showShareMemberModal && memberShares[showShareMemberModal]?.find(
                          (s: any) => s.target_family_id === family.id
                        );
                        const isAlreadyShared = !!(existingShare && (existingShare.status === 'accepted' || existingShare.status === 'pending'));
                        
                        return (
                          <button
                            key={family.id}
                            onClick={() => !isAlreadyShared && handleShareMember(family.id)}
                            disabled={isAlreadyShared}
                            className={`w-full card transition-colors flex items-center gap-3 p-3 ${
                              isAlreadyShared 
                                ? 'opacity-60 cursor-not-allowed' 
                                : 'hover:bg-surface-700'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              family.source === 'linked' 
                                ? 'bg-gradient-to-br from-accent-500 to-amber-500' 
                                : 'bg-gradient-to-br from-primary-500 to-accent-500'
                            }`}>
                              {family.source === 'linked' ? (
                                <LinkIcon className="w-5 h-5 text-white" />
                              ) : (
                                <UsersIcon className="w-5 h-5 text-white" />
                              )}
                            </div>
                            <div className="flex-1 text-left">
                              <p className="font-medium text-white">{family.name}</p>
                              <p className="text-xs text-surface-400">
                                {family.source === 'linked' ? 'Linked family' : `Your role: ${family.my_role}`}
                              </p>
                            </div>
                            {isAlreadyShared ? (
                              <span className={`text-xs px-2 py-1 rounded ${
                                existingShare.status === 'accepted' 
                                  ? 'bg-emerald-500/20 text-emerald-300' 
                                  : 'bg-amber-500/20 text-amber-300'
                              }`}>
                                {existingShare.status === 'accepted' ? 'Shared' : 'Pending'}
                              </span>
                            ) : (
                              <ShareIcon className="w-5 h-5 text-surface-400" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
